/*
 * Copyright 2026 North Carolina State University
 */

import {
  parsePhoneNumberFromString,
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isPossiblePhoneNumber
} from 'libphonenumber-js';

/**
 * Common English country names. Anything not listed falls back to the ISO code.
 * US / GB / CA are pinned to the top of the selector in PhoneInput.
 */
export const COUNTRY_NAMES = {
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia',
  IN: 'India', DE: 'Germany', FR: 'France', CN: 'China', JP: 'Japan',
  MX: 'Mexico', BR: 'Brazil', ES: 'Spain', IT: 'Italy', NL: 'Netherlands',
  IE: 'Ireland', NZ: 'New Zealand', SG: 'Singapore', KR: 'South Korea',
  AE: 'United Arab Emirates', SA: 'Saudi Arabia', ZA: 'South Africa',
  NG: 'Nigeria', KE: 'Kenya', EG: 'Egypt', IL: 'Israel', TR: 'Turkey',
  RU: 'Russia', PL: 'Poland', SE: 'Sweden', NO: 'Norway', DK: 'Denmark',
  FI: 'Finland', CH: 'Switzerland', AT: 'Austria', BE: 'Belgium', PT: 'Portugal',
  GR: 'Greece', CZ: 'Czechia', HU: 'Hungary', RO: 'Romania', UA: 'Ukraine',
  PK: 'Pakistan', BD: 'Bangladesh', LK: 'Sri Lanka', NP: 'Nepal',
  TH: 'Thailand', VN: 'Vietnam', MY: 'Malaysia', ID: 'Indonesia', PH: 'Philippines',
  TW: 'Taiwan', HK: 'Hong Kong', MO: 'Macau', AR: 'Argentina', CL: 'Chile',
  CO: 'Colombia', PE: 'Peru', VE: 'Venezuela', CR: 'Costa Rica', PA: 'Panama',
  GT: 'Guatemala', DO: 'Dominican Republic', PR: 'Puerto Rico', JM: 'Jamaica',
  TT: 'Trinidad and Tobago', BS: 'Bahamas', BB: 'Barbados',
  GH: 'Ghana', TZ: 'Tanzania', UG: 'Uganda', ET: 'Ethiopia', MA: 'Morocco',
  TN: 'Tunisia', DZ: 'Algeria', QA: 'Qatar', KW: 'Kuwait', BH: 'Bahrain',
  OM: 'Oman', JO: 'Jordan', LB: 'Lebanon', IQ: 'Iraq', IR: 'Iran',
  AF: 'Afghanistan', KZ: 'Kazakhstan', UZ: 'Uzbekistan',
  IS: 'Iceland', LU: 'Luxembourg', MT: 'Malta', CY: 'Cyprus', HR: 'Croatia',
  RS: 'Serbia', BG: 'Bulgaria', SK: 'Slovakia', SI: 'Slovenia', LT: 'Lithuania',
  LV: 'Latvia', EE: 'Estonia', BY: 'Belarus', GE: 'Georgia', AM: 'Armenia',
  AZ: 'Azerbaijan', MM: 'Myanmar', KH: 'Cambodia', LA: 'Laos', MN: 'Mongolia',
  FJ: 'Fiji', PG: 'Papua New Guinea', NC: 'New Caledonia',
  CU: 'Cuba', HN: 'Honduras', NI: 'Nicaragua', SV: 'El Salvador',
  BO: 'Bolivia', PY: 'Paraguay', UY: 'Uruguay', EC: 'Ecuador',
  ZW: 'Zimbabwe', BW: 'Botswana', MU: 'Mauritius', SN: 'Senegal', CI: "Côte d'Ivoire"
};

export const PINNED_COUNTRIES = ['US', 'GB', 'CA', 'AU', 'IN'];

export const flagEmoji = (iso) => {
  if (!iso || iso.length !== 2) return '';
  const code = iso.toUpperCase();
  return String.fromCodePoint(
    127397 + code.charCodeAt(0),
    127397 + code.charCodeAt(1)
  );
};

export const countryLabel = (iso) => COUNTRY_NAMES[iso] || iso;

export const getSortedCountries = () => {
  const all = getCountries();
  const pinned = PINNED_COUNTRIES.filter(c => all.includes(c));
  const rest = all
    .filter(c => !pinned.includes(c))
    .sort((a, b) => countryLabel(a).localeCompare(countryLabel(b)));
  return [...pinned, ...rest];
};

/**
 * Turn whatever we have stored (E.164, US 10-digit, formatted) into E.164
 * when we can, otherwise return the original string.
 */
export const toE164 = (value, defaultCountry = 'US') => {
  if (!value) return '';
  const str = String(value).trim();
  if (!str || str.toLowerCase() === 'please update in profile') return '';

  const parsed = parsePhoneNumberFromString(str, defaultCountry);
  if (parsed) return parsed.number;

  const digits = str.replace(/\D/g, '');
  if (digits.length === 10) {
    const us = parsePhoneNumberFromString(digits, 'US');
    if (us) return us.number;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    const us = parsePhoneNumberFromString('+' + digits);
    if (us) return us.number;
  }
  if (str.startsWith('+')) return str;
  return str;
};

export const detectCountry = (value, fallback = 'US') => {
  if (!value) return fallback;
  const parsed = parsePhoneNumberFromString(String(value), fallback);
  return parsed?.country || fallback;
};

/**
 * National digits/formatting for the given country, used while the user types.
 */
export const formatNational = (value, country = 'US') => {
  if (!value) return '';
  try {
    const formatter = new AsYouType(country);
    const parsed = parsePhoneNumberFromString(String(value), country);
    const input = parsed ? parsed.nationalNumber : String(value).replace(/[^\d+]/g, '');
    return formatter.input(input);
  } catch {
    return String(value);
  }
};

export const isValidInternationalPhone = (value, country = 'US') => {
  if (!value) return false;
  const e164 = toE164(value, country);
  if (!e164) return false;
  try {
    if (isPossiblePhoneNumber(e164)) return true;
    const parsed = parsePhoneNumberFromString(e164, country);
    return !!(parsed && (parsed.isValid() || parsed.isPossible()));
  } catch {
    return false;
  }
};

export const formatInternational = (value, fallback = 'US') => {
  if (!value) return '';
  const parsed = parsePhoneNumberFromString(String(value), fallback)
    || parsePhoneNumberFromString(toE164(value, fallback));
  if (parsed) {
    try {
      return parsed.formatInternational();
    } catch {
      return parsed.number;
    }
  }
  return String(value);
};

export const callingCode = (iso) => {
  try {
    return `+${getCountryCallingCode(iso)}`;
  } catch {
    return '';
  }
};
